import { FC, useRef, useState } from 'react';
import { Box, Button, Typography } from '@mui/material';
import useVehicle from 'hooks/lp/data/vehicle/useVehicle';
import { useLayoutContext } from 'contexts/LayoutContext';
import CurrencyRate from 'components/CurrencyRate';
import { CurrencyCode } from 'further-types/lp/vehicle';
import { CurrencyRateDetails } from 'further-types/lp/transaction';
import { CalendarDay, pluralize } from 'further-ui/utils';
import PayablesOrReceivables from './PayablesOrReceivables';
import Cash from './Cash';
import Page from 'components/new/Page';
import ProcessingPage from 'components/new/ProcessingPage';
import { CheckCircle, ShowChartOutlined } from '@mui/icons-material';
import Chip from 'components/Chip';
import TabsPanel from 'further-ui/components/TabsPanel';
import { styled } from '@mui/material/styles';
import PageContent from 'components/PageContent';
import PageSection from 'components/PageSection';
import useHandleTrpcMutation from 'hooks/ui/useHandleTrpcMutation';
import api from 'lib/trpcClient';
import { useHistory, useLocation } from 'react-router-dom';
import {
  createLpPeriodEndReviewDetailRoute,
  LP_PERIOD_END_REVIEW,
} from 'adminConstants/routes';
import { useDisclosure } from 'further-ui/hooks';
import useEqualisations from 'hooks/lp/data/equalisations/useEqualisations';
import PendingDraftEqualisationsModal from 'components/PendingDraftEqualisationsModal';
import ButtonGroup from 'components/ButtonGroup';

enum PostUnrealisedFXGainsTab {
  PayablesOrReceivables = 'payablesOrReceivables',
  Cash = 'cash',
}

const tabsData = {
  [PostUnrealisedFXGainsTab.PayablesOrReceivables]: {
    title: 'Payables or receivables',
  },
  [PostUnrealisedFXGainsTab.Cash]: {
    title: 'Cash',
  },
};

const TabLabelWrapper = styled('div')({
  display: 'flex',
  alignItems: 'center',
  gap: 8,
});

const PostUnrealisedFXGains: FC = () => {
  const { selectedVehicleId } = useLayoutContext();
  const { vehicle } = useVehicle(selectedVehicleId);
  const { search } = useLocation();
  const searchParams = new URLSearchParams(search);
  const periodStartParam = searchParams.get('periodStart');
  const periodEndParam = searchParams.get('periodEnd');
  const periodId = searchParams.get('periodId') ?? undefined;
  const periodStart = periodStartParam
    ? new CalendarDay(periodStartParam)
    : null;
  const periodEnd = periodEndParam ? new CalendarDay(periodEndParam) : null;
  const history = useHistory();

  const [currencyRates, setCurrencyRates] = useState<
    Array<[CurrencyCode, CurrencyRateDetails]>
  >([]);
  const [currencyRateOverrides, setCurrencyRateOverrides] = useState<
    Map<CurrencyCode, CurrencyRateDetails>
  >(new Map());

  const [tabCounts, setTabCounts] = useState<
    Partial<Record<PostUnrealisedFXGainsTab, number | undefined>>
  >({});

  const [isProcessingApproval, setIsProcessingApproval] = useState(false);
  const [isPending, setIsPending] = useState(false);

  const approveRef = useRef<(() => Promise<void>) | null>(null);

  const { hasEqualisationInDraftState } = useEqualisations(
    selectedVehicleId ?? '',
  );
  const confirmDialog = useDisclosure();

  const handleMutation = useHandleTrpcMutation<string>();
  const { mutateAsync: updateChecklistItem } =
    api.accountingPeriod.updateChecklistItem.useMutation(
      handleMutation({ refetchType: 'active' }),
    );

  const handleApproveSuccess = async () => {
    try {
      if (!selectedVehicleId || !periodId) {
        throw new Error('Invalid vehicle or period ID');
      }
      await updateChecklistItem({
        vehicleId: selectedVehicleId,
        id: periodId,
        item: 'unrealisedFxGainsAndLosses',
        confirmed: true,
      });
      history.push(createLpPeriodEndReviewDetailRoute(periodId));
    } catch {
      setIsProcessingApproval(false);
    }
  };

  const handleApproveClick = () => {
    if (hasEqualisationInDraftState) {
      confirmDialog.onOpen();
    } else {
      void handleApprove();
    }
  };

  const handleCurrenciesLoaded = (
    currencies: Array<[CurrencyCode, CurrencyRateDetails]>,
  ) => {
    setCurrencyRates(currencies);
  };

  const handlePayablesCountChange = (count: number) =>
    setTabCounts((prev) => ({
      ...prev,
      [PostUnrealisedFXGainsTab.PayablesOrReceivables]: count,
    }));

  const handleCashCountChange = (count: number) =>
    setTabCounts((prev) => ({
      ...prev,
      [PostUnrealisedFXGainsTab.Cash]: count,
    }));

  const handleApprove = async () => {
    setIsPending(true);
    setIsProcessingApproval(true);
    try {
      await approveRef.current?.();
    } catch {
      setIsProcessingApproval(false);
    } finally {
      setIsPending(false);
    }
  };

  if (!vehicle) {
    return null;
  }

  const handleExit = () => {
    history.push(
      periodId
        ? createLpPeriodEndReviewDetailRoute(periodId)
        : LP_PERIOD_END_REVIEW,
    );
  };

  return (
    <Page
      heading="Post unrealised FX gains and losses"
      headingIcon={ShowChartOutlined}
      spacing="lg"
      withHeadingDivider
      actionBar={
        <ButtonGroup align="stretch">
          <Button variant="outlined" onClick={handleExit}>
            Exit
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={handleApproveClick}
            disabled={isPending}
          >
            Approve and mark as reviewed
          </Button>
        </ButtonGroup>
      }
    >
      <PageContent withSpacing>
        {isProcessingApproval ? (
          <PageSection withSpacing>
            <ProcessingPage
              title="Approving transactions"
              subtitle="This will only take a moment."
            />
          </PageSection>
        ) : (
          <>
            <PageSection withSpacing>
              <Box>
                <Typography variant="h6" color="text.secondary">
                  {pluralize(
                    'Transaction currency rate',
                    currencyRates.length ?? 0,
                  )}
                  :
                </Typography>
                {currencyRates.map(([currencyCode, fxRate], index) => (
                  <CurrencyRate
                    key={`${currencyCode}-${index}`}
                    fxRate={fxRate}
                    currency={currencyCode}
                    vehicle={vehicle}
                    isFetchingRate={false}
                    onRateChange={(rateDetails) => {
                      setCurrencyRateOverrides((prev) => {
                        const newMap = new Map(prev);
                        newMap.set(currencyCode, rateDetails);
                        return newMap;
                      });
                    }}
                  />
                ))}
              </Box>
            </PageSection>

            <PageSection withSpacing>
              <TabsPanel
                tabs={Object.entries(tabsData).map(([tab, { title }]) => ({
                  key: tab as PostUnrealisedFXGainsTab,
                  label: (
                    <TabLabelWrapper>
                      {title}
                      {tabCounts[tab as PostUnrealisedFXGainsTab] !==
                        undefined &&
                        (tabCounts[tab as PostUnrealisedFXGainsTab] === 0 ? (
                          <CheckCircle color="success" fontSize="small" />
                        ) : (
                          <Chip variant="tabIndicator" small>
                            {tabCounts[tab as PostUnrealisedFXGainsTab] ?? 0}
                          </Chip>
                        ))}
                    </TabLabelWrapper>
                  ),
                  content:
                    tab === PostUnrealisedFXGainsTab.PayablesOrReceivables ? (
                      <PayablesOrReceivables
                        vehicleId={vehicle._id}
                        periodStart={periodStart ?? new CalendarDay(new Date())}
                        periodEnd={periodEnd ?? new CalendarDay(new Date())}
                        currencyRateOverrides={currencyRateOverrides}
                        onCurrenciesLoaded={handleCurrenciesLoaded}
                        onApproveSuccess={handleApproveSuccess}
                        onTotalCountChange={handlePayablesCountChange}
                        approveRef={approveRef}
                      />
                    ) : (
                      <Cash
                        vehicleId={vehicle._id}
                        periodStart={periodStart ?? new CalendarDay(new Date())}
                        periodEnd={periodEnd ?? new CalendarDay(new Date())}
                        currencyRateOverrides={currencyRateOverrides}
                        onCurrenciesLoaded={handleCurrenciesLoaded}
                        onApproveSuccess={handleApproveSuccess}
                        onTotalCountChange={handleCashCountChange}
                        approveRef={approveRef}
                      />
                    ),
                }))}
                defaultTab={PostUnrealisedFXGainsTab.PayablesOrReceivables}
              />
            </PageSection>
          </>
        )}
      </PageContent>
      {hasEqualisationInDraftState && (
        <PendingDraftEqualisationsModal
          isOpen={confirmDialog.isOpen}
          onClose={confirmDialog.onClose}
          onConfirm={() => void handleApprove()}
          loading={isPending}
        />
      )}
    </Page>
  );
};

export default PostUnrealisedFXGains;
